import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import ReactEcharts from "echarts-for-react";

function DashboardGraphs(props) {
	const [P10Data, setP10Data] = useState(null);
	const [graphConfig, setGraphConfig] = useState({});
	const [graphSensorConfig, setgraphSensorConfig] = useState({});
	const [graphWWSensorConfig, setGraphWWSensorConfig] = useState({});
	const [hasWWGraph, setHasWWGraph] = useState(false);
	const [showWWGraph, setShowWWGraph] = useState(false);

	const irrIcon = "wi wi-day-sunny";
	const wwIcon = "wi wi-strong-wind";

	const wwStyle = {
		fontSize: "2.2rem",
	};

	const irrName = "Irradiance";
	const irrDataKey = "irradiance";
	const ambName = "Ambient temp";
	const ambDataKey = "ambient_temp";
	const modName = "Module temp";
	const modDataKey = "module_temp";
	const pvName = "PV power";
	const windName = "Wind Speed";
	const windDataKey = "wind_speed";
	const waterName = "Water Level";
	const waterDataKey = "water_level";

	const irrSensorsName = ["irrNo", "ambNo", "modNo"];
	const WWSensorsName = ["windNo", "waterNo"];

	/**
	 * Get legends of config
	 * @param {Keys of sensors object} keys
	 * @param {Values of sensors object} vals
	 */
	const getLegends = (keys, vals, isWWgraph) => {
		let legends = [];
		let validSensors = [];

		if (!isWWgraph) validSensors = irrSensorsName;
		else validSensors = WWSensorsName;

		keys.map((key, i) => {
			const count = vals[i];

			if (validSensors.includes(key)) {
				switch (key) {
					case "irrNo":
						legends = [...legends, ...formatLegendObj(count, irrName)];
						break;
					case "ambNo":
						legends = [...legends, ...formatLegendObj(count, ambName)];
						break;
					case "modNo":
						legends = [...legends, ...formatLegendObj(count, modName)];
						break;
					case "windNo":
						legends = [...legends, ...formatLegendObj(count, windName)];
						break;
					case "waterNo":
						legends = [...legends, ...formatLegendObj(count, waterName)];
						break;
					default:
						break;
				}
			}
		});

		return legends;
	};

	/**
	 * Format grapg legend object
	 * @param {number} count
	 * @param {string} name
	 * @returns Graph legend object
	 */
	const formatLegendObj = (count, name) => {
		let legends = [];
		if (count === 1) legends.push(name);
		else {
			for (let i = 0; i < count; i++) {
				legends.push(`${name} ${i + 1}`);
			}
		}

		return legends;
	};

	/**
	 * Get series of graph config
	 * @param {Keys of sensors object} keys
	 * @param {Values of sensors object} vals
	 * @param {P10 data} P10Data
	 */
	const getSeries = (keys, vals, P10Data, isWWgraph) => {
		let series = [];
		let validSensors = [];

		if (!isWWgraph) validSensors = irrSensorsName;
		else validSensors = WWSensorsName;

		keys.map((key, i) => {
			const count = vals[i];

			if (validSensors.includes(key)) {
				switch (key) {
					case "irrNo":
						series = [
							...series,
							...formatSeriesObj(count, irrName, irrDataKey, P10Data),
						];
						break;
					case "ambNo":
						series = [
							...series,
							...formatSeriesObj(count, ambName, ambDataKey, P10Data),
						];
						break;
					case "modNo":
						series = [
							...series,
							...formatSeriesObj(count, modName, modDataKey, P10Data),
						];
						break;
					case "windNo":
						series = [
							...series,
							...formatSeriesObj(count, windName, windDataKey, P10Data),
						];
						break;
					case "waterNo":
						series = [
							...series,
							...formatSeriesObj(count, waterName, waterDataKey, P10Data),
						];
						break;
					default:
						break;
				}
			}
		});

		return series;
	};

	const formatSeriesObj = (count, name, dataKey, P10Data) => {
		let series = [];
		if (count === 1) series.push(getSingleSeries(name, dataKey, P10Data));
		else {
			for (let i = 0; i < count; i++) {
				const legend = `${name} ${i + 1}`;
				const multiDataKey = `${dataKey}_${i + 1}`;
				const singleSeries = getSingleSeries(legend, multiDataKey, P10Data);
				series.push(singleSeries);
			}
		}
		return series;
	};

	/**
	 * Get single series of graph config
	 * @param {Legends of graph} legend
	 * @param {Key of data} dataKey
	 * @param {P10 data} P10Data
	 */
	const getSingleSeries = (legend, dataKey, P10Data) => ({
		name: legend,
		type: "line",
		lineStyle: {
			normal: {
				width: 1.2,
			},
		},
		symbolSize: 2,
		showSymbol: false,
		yAxisIndex:
			dataKey.includes(irrDataKey) || dataKey.includes(waterDataKey) ? 1 : 0,
		data: P10Data[dataKey],
	});

	/**
	 * Build graph config with parameters passed
	 * @param {array} color
	 * @param {array} legends
	 * @param {array} yAxis
	 * @param {array} series
	 * @returns graph config
	 */
	const buildGraphConfig = (color, legends, yAxis, series) => {
		return {
			color,
			tooltip: {
				trigger: "axis",
				axisPointer: {
					animation: true,
					type: "cross",
				},
			},
			legend: {
				data: legends,
			},
			grid: {
				left: "3%",
				right: "4%",
				bottom: "3%",
				containLabel: true,
			},
			toolbox: {
				feature: {
					magicType: {
						show: true,
						title: {
							line: "line",
							bar: "bar",
						},
						type: ["line", "bar"],
					},
					restore: {
						title: "reload",
					},
				},
			},
			dataZoom: [
				{
					type: "slider",
					filterMode: "filter",
				},
				{
					type: "inside",
					filterMode: "filter",
				},
			],
			grid: {
				top: 70,
				bottom: 70,
			},
			xAxis: {
				type: "category",
				boundaryGap: false,
				data: P10Data.dates,
			},
			yAxis,
			series,
		};
	};

	useEffect(() => {
		if (props.data != null) setP10Data(props.data);
		return () => setShowWWGraph(false);
	}, [props.data]);

	useEffect(() => {
		if (P10Data != null) {
			const sensorNumbers = props.sensorNumbers;
			const hasWWGraph = sensorNumbers.windNo > 0 || sensorNumbers.waterNo > 0;

			setHasWWGraph(hasWWGraph);

			const keys = Object.keys(sensorNumbers);
			const vals = Object.values(sensorNumbers);

			// PV x Irradiance graph parameters
			const pvIrrGraphColors = ["#f28a10", "#8dc63f"];
			const pvIrrGraphLegends = [irrName, pvName];
			const pvIrrGraphYAxis = [
				{
					type: "value",
					name: pvName,
					splitLine: {
						show: false,
					},
					min: "-1",
					axisLabel: {
						formatter: "{value} kW",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				},
				{
					type: "value",
					name: irrName,
					splitLine: {
						show: false,
					},
					axisLabel: {
						formatter: "{value} W/m\xB2",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				},
			];
			const pvIrrGraphSeries = [
				{
					name: pvName,
					type: "line",
					areaStyle: {},
					data: P10Data.solar_power,
				},
				{
					name: irrName,
					type: "line",
					yAxisIndex: 1,
					data: P10Data.irradiance,
				},
			];

			// PV x Irradiance graph config
			const pvIrrSensorConfig = buildGraphConfig(
				pvIrrGraphColors,
				pvIrrGraphLegends,
				pvIrrGraphYAxis,
				pvIrrGraphSeries
			);

			setGraphConfig(pvIrrSensorConfig);

			// Irradiance graph parameters
			const irrGraphColors = [
				"#86bc25",
				"#007000",
				"#f28a10",
				"#ffc602",
				"#1693b7",
			];
			const irrGraphLegends = getLegends(keys, vals, false);
			const irrGraphYAxis = [
				{
					type: "value",
					name: "Temperature",
					splitLine: {
						show: false,
					},
					axisLabel: {
						formatter: "{value} °C",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				},
				{
					type: "value",
					name: irrName,
					splitLine: {
						show: false,
					},
					axisLabel: {
						formatter: "{value} W/m\xB2",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				},
			];
			const irrGraphSeries = getSeries(keys, vals, P10Data, false);

			// Irradiance graph config
			const irrSensorConfig = buildGraphConfig(
				irrGraphColors,
				irrGraphLegends,
				irrGraphYAxis,
				irrGraphSeries
			);

			setgraphSensorConfig(irrSensorConfig);

			if (hasWWGraph) {
				// Wind / Water graph parameters
				const WWGraphColors = ["#189672", "#5ccdf1"];
				const WWGraphLegends = getLegends(keys, vals, true);
				const showWindYAxis = sensorNumbers.windNo > 0;
				const showWaterYAxis = sensorNumbers.waterNo > 0;

				const windYAxis = {
					type: "value",
					name: "Speed",
					splitLine: {
						show: false,
					},
					axisLabel: {
						formatter: "{value} m/s",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				};

				const waterYAxis = {
					type: "value",
					name: "Level",
					splitLine: {
						show: false,
					},
					axisLabel: {
						formatter: "{value} m",
					},
					axisLine: { show: true },
					axisTick: {
						show: true,
					},
				};

				const WWGraphYAxis = [];

				if (showWindYAxis) WWGraphYAxis.push(windYAxis);
				if (showWaterYAxis) WWGraphYAxis.push(waterYAxis);

				const WWGraphSeries = getSeries(keys, vals, P10Data, true);

				// Wind / Water graph config
				const WWSensorConfig = buildGraphConfig(
					WWGraphColors,
					WWGraphLegends,
					WWGraphYAxis,
					WWGraphSeries
				);

				setGraphWWSensorConfig(WWSensorConfig);
			}
		}

		return () => {
			setHasWWGraph(false);
			setShowWWGraph(false);
		};
	}, [P10Data]);

	return (
		<Row style={{ margin: "20px 0 40px 0" }}>
			<Col md="6"></Col>
			<Col md="6">
				{hasWWGraph && (
					<div id="switch-container">
						<Col md="4"></Col>
						<Col md="2">
							<i
								className={!showWWGraph ? `${irrIcon} irr-color` : irrIcon}
								title="Irradiance/Temp sensors"
								style={wwStyle}
							></i>
						</Col>
						<Col md="4">
							<div>
								<label className="switch">
									<input
										type="checkbox"
										id="test"
										onChange={() => setShowWWGraph(!showWWGraph)}
										checked={showWWGraph}
									/>
									<span className="slider round"></span>
								</label>
							</div>
						</Col>
						<Col md="2" id="left-toggle">
							<i
								className={showWWGraph ? `${wwIcon} ww-color` : wwIcon}
								title="Wind/Water sensors"
								style={wwStyle}
							></i>
						</Col>
					</div>
				)}
			</Col>
			<Col md="6">
				<ReactEcharts option={graphConfig} notMerge={true} lazyUpdate={true} />
			</Col>
			<Col md="6">
				<ReactEcharts
					option={showWWGraph ? graphWWSensorConfig : graphSensorConfig}
					notMerge={true}
					lazyUpdate={true}
				/>
			</Col>
		</Row>
	);
}

export default DashboardGraphs;
